import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { Analytics, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { StateService } from '@uirouter/core';
import { SelectedPlanService } from 'shared/services/selected-plan';
import { StoreAction } from 'store/actions';
import { LevelMoveOrderReview } from 'store/level-move/models/order-review';
import { CmsLevelMoveStart } from 'store/cms/models';

/**
 * Represents the component for level move order review.
 * This component is responsible for displaying the initial page data and handling user interactions.
 */
@Component({
    selector: 'xm-level-move-review-order',
    styleUrls: ['./level-move-review-order.scss'],
    templateUrl: './level-move-review-order.html',
    standalone: false
})
export class XmLevelMoveReviewOrder implements OnInit, OnDestroy {
    public pageData: CmsLevelMoveStartData = {
        header: '',
        planGroupInfo: []
    };
    public cmsData: LevelMoveOrderReview;
    public pageTitle: string;
    public upgradeBtn: string;
    public backBtn: string;
    public selectedPlans: BillPreviewPayload[];
    public isLoading: boolean = true;
    public expandIconOptions: MediaImageOptions;

    private xmStore: XmStore;
    private subscriptions: Subscription[] = [];
    private state: StateService;
    private planOverrides: LevelMoveOverride[];
    private selectedPlanService: SelectedPlanService;
    private analytics: Analytics;

    constructor(xmStore: XmStore, state: StateService, selectedPlanService: SelectedPlanService, analytics: Analytics) {
        Object.assign(this, { xmStore, state, selectedPlanService, analytics });
    }

    public ngOnInit(): void {
        const levelMoveOverrides: LevelMoveOverride[] = this.selectedPlanService.getSelectedPlans();
        this.selectedPlans = levelMoveOverrides.map((override: LevelMoveOverride) => ({
            lineId: override.lineId,
            newPlanId: override.planId
        }));

        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsLevelMoveStart>(CmsLevelMoveStart), (result: CmsLevelMoveStart) => {

            this.expandIconOptions = {
                mobile: Cloudinary.generateMediaFromCms(result.expandIcon)
            };
        }));

        this.xmStore.persist<LevelMoveOrderReview>(StoreAction.POST_LEVEL_MOVE_ORDER_REVIEW, { changes: this.selectedPlans })
            .then((result: LevelMoveOrderReview) => {
                this.cmsData = result;
                this.isLoading = false;
                this.pageTitle = this.cmsData.orderSummary.pageHeader;
                this.backBtn = this.cmsData.orderSummary.ctas.backCta;
                this.upgradeBtn = this.cmsData.orderSummary.ctas.confirmCta;
                this.sendAnalyticsData();
            })
            .catch(() => {
                this.startUpgrade();
            });
    }

    /**
     * Cancels the level move and navigates to the landing page.
     */
    public onBack(): void {
        this.state.go('account.settings.level-move.eligibility');
    }

    /**
     * Initiates the level move process.
     */
    public startUpgrade(): void {
        this.planOverrides = this.selectedPlanService.getSelectedPlans();
        this.state.go('account.settings.level-move.in-progress', { planOverrides: this.planOverrides });
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
    }

    /**
     * Sends analytics data for the review order page.
     */
    private sendAnalyticsData(): void {
        if (!this.cmsData?.analyticsData) {
            return;
        }

        const productInfo: object[] = this.cmsData.analyticsData.map((data: PlanChangeAnalyticsPayload) => ({
            previousPlan: data.previousPlan || '',
            newPlan: data.newPlan || '',
            changeType: data.changeType || '',
            priceChanged: data.priceChanged || '0'
        }));

        this.analytics.sendCustomEvent('segment-change-review', {
            cart: {
                products: {
                    productInfo: [...productInfo]
                }
            }
        }, true);
    }
}
